/**
 * 同站判定。
 *
 * 为什么需要它：会话是 httpOnly Cookie，且默认 `SameSite=Lax`。
 * `Lax` 只在**同站**请求上发送 Cookie —— 浏览器按「可注册域名」（registrable domain）判断，
 * 而不是按主机名。于是：
 *
 *   前端 staff.havorix.cn → API api.staff.havorix.cn   同站（都是 havorix.cn）  ✅ Cookie 会带上
 *   前端 havorix-staff.pages.dev → API api.staff.havorix.cn   跨站           ❌ Cookie 被丢弃
 *
 * 跨站时的表现特别容易误导：登录接口返回 200，界面也切成了已登录，
 * 但之后每个请求都是 401 —— 看起来像「登录坏了」，其实是 Cookie 没发出去。
 * 与其让人对着日志猜，不如在界面上直接说清楚。
 *
 * 这里不做完整的 Public Suffix List 解析：只需要覆盖项目实际用到的后缀形态，
 * 遇到不认识的后缀按「最后两段」处理，最坏情况是把某些跨站误判成同站
 * （那就退回原来的表现），不会把同站误判成跨站而误报。
 */

/** 常见的两段式公共后缀：此时可注册域名要取三段。 */
const TWO_LEVEL_SUFFIXES = new Set([
  'com.cn',
  'net.cn',
  'org.cn',
  'gov.cn',
  'edu.cn',
  'ac.cn',
  'com.hk',
  'com.tw',
  'co.uk',
  'org.uk',
  'co.jp',
  'or.jp',
  'com.au',
  'co.nz',
]);

/**
 * 每个子域都是独立站点的托管后缀 —— 这正是 Pages 的形态：
 * `a.pages.dev` 与 `b.pages.dev` 互不同站，所以必须把 `pages.dev` 整段当作后缀。
 */
const PER_TENANT_SUFFIXES = ['pages.dev', 'workers.dev', 'vercel.app', 'netlify.app'];

/** 取出可注册域名（site），取不到时返回空串。 */
export function siteOf(hostname: string): string {
  const host = hostname.split(':')[0]?.trim().toLowerCase().replace(/\.$/, '') ?? '';
  if (host === '') return '';

  for (const suffix of PER_TENANT_SUFFIXES) {
    if (host === suffix) return host;
    if (host.endsWith(`.${suffix}`)) {
      const prefix = host.slice(0, host.length - suffix.length - 1);
      const last = prefix.split('.').pop() ?? '';
      return last === '' ? host : `${last}.${suffix}`;
    }
  }

  const parts = host.split('.');
  if (parts.length <= 2) return host;

  const lastTwo = parts.slice(-2).join('.');
  if (TWO_LEVEL_SUFFIXES.has(lastTwo)) return parts.slice(-3).join('.');
  return lastTwo;
}

/** 两个主机名是否同站。任一侧取不到可注册域名时返回 false（宁可不报也不乱报）。 */
export function isSameSite(hostA: string, hostB: string): boolean {
  const siteA = siteOf(hostA);
  const siteB = siteOf(hostB);
  if (siteA === '' || siteB === '') return false;
  return siteA === siteB;
}

/** 从绝对地址里取主机名；相对地址或非法地址返回空串。 */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}
